import type * as React from "react";
import { cn } from "../../lib/cn";

/**
 * Skeleton — DRY pulse placeholder for every async surface.
 *
 * Phase 4.2 consolidation (June 11, 2026): four sections of the project
 * track each carried a hand-rolled `animate-pulse` skeleton shape
 * (Artifact gallery, Memory timeline, Automations list, Templates
 * grid). They drifted on padding / corner-radius / row count. This
 * primitive owns the brand-correct shape (bg-ink-1/[0.06] for the
 * darker fill, bg-ink-1/[0.04] for the secondary stripe — same tokens
 * used by AutoSyncBadge and DocumentCardV2 skeletons), the consumers
 * only declare the layout (`variant`) and count.
 *
 * Variants:
 *   - ``block``     — single rounded rectangle. Pass `className` to
 *                     size it (e.g. ``className="h-3 w-2/3"``). Used as
 *                     a building block by the surfaces below.
 *   - ``card-row``  — icon tile + 2-line text block + optional trailing
 *                     stripe. The shape used by every per-row card in
 *                     the project sections.
 *   - ``card-grid`` — same row shape stacked into the responsive grid
 *                     (1-col mobile, 2-col >=sm). ``count`` controls how
 *                     many placeholders render.
 *
 * The primitive is purely presentational — never owns query state.
 * Callers wrap it in their own ``query.isLoading`` branch.
 */
export type SurfaceSkeletonVariant = "block" | "card-row" | "card-grid";

export interface SurfaceSkeletonProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "children"> {
  /** Layout to render. ``block`` for a single rectangle, ``card-row``
   *  for a per-row card placeholder, ``card-grid`` for the responsive
   *  grid the artifact gallery / templates browser use. */
  variant?: SurfaceSkeletonVariant;
  /** Number of rows to render (``card-row`` / ``card-grid``). Default 4. */
  count?: number;
  /** Optional accessible label for the wrapping ``role=status``. */
  ariaLabel?: string;
}

const BASE_DARK = "bg-ink-1/[0.06]";
const BASE_LIGHT = "bg-ink-1/[0.04]";

/** A single rounded placeholder rectangle (composable). */
const Block: React.FC<{ className?: string; tone?: "dark" | "light" }> = ({
  className,
  tone = "dark",
}) => (
  <div
    className={cn(
      "animate-pulse rounded",
      tone === "dark" ? BASE_DARK : BASE_LIGHT,
      className,
    )}
    aria-hidden
  />
);

/** Icon tile (9x9) + 2-line text block. The default per-row shape. */
const CardRow: React.FC = () => (
  <div
    className={cn(
      "flex h-[68px] items-start gap-3 rounded-xl border border-hairline bg-white/40 p-3.5",
    )}
    aria-hidden
  >
    <Block className="h-9 w-9 shrink-0 rounded-md" />
    <div className="flex-1 space-y-2 pt-0.5">
      <Block className="h-3 w-2/3" />
      <Block className="h-2.5 w-1/3" tone="light" />
    </div>
  </div>
);

/**
 * SurfaceSkeleton — the entry point. ``variant`` picks the shape;
 * ``count`` controls how many rows render for the list variants.
 *
 * Named ``SurfaceSkeleton`` (not the bare ``Skeleton``) because the
 * primitives layer already exports a low-level ``Skeleton`` (shadcn-
 * style rect/text/circle/pill). This compound primitive composes that
 * one into the per-row / per-grid shapes the workspace surfaces share.
 */
export const SurfaceSkeleton: React.FC<SurfaceSkeletonProps> = ({
  variant = "block",
  count = 4,
  ariaLabel,
  className,
  ...rest
}) => {
  if (variant === "block") {
    return <Block className={className} />;
  }
  if (variant === "card-row") {
    return (
      <div
        role="status"
        aria-busy
        aria-label={ariaLabel}
        className={cn("flex flex-col gap-2", className)}
        {...rest}
      >
        {Array.from({ length: count }).map((_, i) => (
          <CardRow key={i} />
        ))}
      </div>
    );
  }
  // card-grid — responsive 1 / 2 column grid of CardRow placeholders.
  return (
    <div
      role="status"
      aria-busy
      aria-label={ariaLabel}
      className={cn(
        "grid grid-cols-1 gap-2.5 sm:grid-cols-2",
        className,
      )}
      {...rest}
    >
      {Array.from({ length: count }).map((_, i) => (
        <CardRow key={i} />
      ))}
    </div>
  );
};

SurfaceSkeleton.displayName = "SurfaceSkeleton";
